import { useCallback } from 'react';

import { AppDispatch } from '@redux/store';
import {
  ParticipantServerData,
  ParticipantStatus,
  ParticipantRole,
  SpeakerServerData,
  Speaker,
  TeamType,
  UpdateParticipantRoleSocketData,
} from '@typings/participant';
import {
  Show as ShowDataType,
  Topic,
  ParticipantStatistics,
  ShowServerData,
  HostStatistics,
} from '@typings/show';
import { participantsSlice } from '@redux/state/participantsSlice';
import { usersSlice } from '@redux/state/usersSlice';
import { showsSlice } from '@redux/state/showsSlice';
import { speakersSlice } from '@redux/state/speakersSlice';
import { topicsSlice } from '@redux/state/topicsSlice';
import { topUsersMapper } from '@redux/helpers/participants';

type SocketHandlerDependencies = {
  dispatch: AppDispatch;
  user: { id: string } | null;
  show: { id: string } | null;
  rightSpeakerId: string | null;
  leftSpeakerId: string | null;
  onClientRoleChanged: (role: ParticipantRole) => void;
  turnOnMicrophone: (muted: boolean) => void;
  isHost: boolean;
  isCoHost: boolean;
};

export const createSocketHandlers = (deps: SocketHandlerDependencies) => {
  const {
    dispatch,
    user,
    show,
    rightSpeakerId,
    leftSpeakerId,
    onClientRoleChanged,
    turnOnMicrophone,
    isHost,
    isCoHost,
  } = deps;

  const handleAnonymousParticipantJoin = (
    payload: ParticipantServerData & {
      isAnonymous?: boolean;
      token?: string;
    },
  ) => {
    if (payload.isAnonymous && payload.token !== user?.id) {
      dispatch(showsSlice.actions.addParticipantToShow(payload));
      return true;
    }
    return false;
  };

  const handleParticipantJoin = (payload: ParticipantServerData) => {
    if (payload.profile?.id !== user?.id) {
      const {
        profile: profileData,
        show: showData,
        ...restParticipantData
      } = payload;

      if (!profileData?.id || !showData?.id) {
        console.log('Invalid participant join payload:', payload);
        return false;
      }

      dispatch(
        participantsSlice.actions.setParticipantDataById({
          ...restParticipantData,
          profileId: profileData.id,
          showId: showData.id,
        }),
      );
      dispatch(usersSlice.actions.setUserDataById(payload.profile));
      dispatch(showsSlice.actions.addParticipantToShow(payload));
      return true;
    }
    return false;
  };

  const handleParticipantLeave = (payload: {
    id: string;
    speaker: { id: string };
    profile: { id: string };
    status: ParticipantStatus;
    role: ParticipantRole;
    isAnonymous?: boolean;
  }) => {
    if (
      payload.status === ParticipantStatus.Left &&
      payload.role !== ParticipantRole.CoHost
    ) {
      dispatch(
        showsSlice.actions.removeParticipantFromShow({
          id: payload.id,
          show: { id: show?.id || '' },
        }),
      );
    }

    if (payload.isAnonymous) {
      return;
    }

    dispatch(
      participantsSlice.actions.setParticipantDataById({
        id: payload.id,
        status: payload.status,
        role: payload.role,
      }),
    );

    if (rightSpeakerId === payload.speaker?.id) {
      dispatch(
        showsSlice.actions.setShowDataById({
          id: show?.id || '',
          rightTeamSpeakerId: null,
        }),
      );
    }

    if (leftSpeakerId === payload.speaker?.id) {
      dispatch(
        showsSlice.actions.setShowDataById({
          id: show?.id || '',
          leftTeamSpeakerId: null,
        }),
      );
    }

    if (payload.profile.id === user?.id) {
      onClientRoleChanged(ParticipantRole.Listener);
    }
  };

  const handleParticipantStatusUpdate = (payload: {
    id: string;
    status: ParticipantStatus;
    profile: { id: string };
  }) => {
    dispatch(
      participantsSlice.actions.setParticipantDataById({
        id: payload.id,
        status: payload.status,
      }),
    );

    if (payload.profile.id === user?.id) {
      turnOnMicrophone(
        payload.status === ParticipantStatus.Muted ||
          payload.status === ParticipantStatus.ForceMuted,
      );
    }
  };

  const handleShowUpdate = (
    payload: {
      speakers?: SpeakerServerData[];
    } & ShowDataType,
  ) => {
    const { speakers, ...showData } = payload;
    let leftSpeaker = leftSpeakerId;
    let rightSpeaker = rightSpeakerId;

    if (speakers !== undefined) {
      const speakersData = speakers.reduce(
        (acc: Record<string, Speaker>, speaker: SpeakerServerData) => {
          acc[speaker.id] = {
            id: speaker.id,
            side: speaker.side,
            likesCount: 0,
            profileId: speaker.user.id,
            topicId: speaker.topic?.id,
            participantId: speaker.participant?.id,
          };

          speaker.side === TeamType.Blue
            ? (leftSpeaker = speaker.id)
            : (rightSpeaker = speaker.id);

          return acc;
        },
        {},
      );

      dispatch(
        speakersSlice.actions.setSpeakersData(Object.values(speakersData)),
      );
    }

    dispatch(
      showsSlice.actions.setShowDataById({
        ...showData,
        leftTeamSpeakerId: leftSpeaker,
        rightTeamSpeakerId: rightSpeaker,
      }),
    );
  };

  const handleEndShow = (
    data: Partial<ShowDataType> & {
      topics: Topic[];
      statistics: ParticipantStatistics;
    },
  ) => {
    if (!isHost && !isCoHost) {
      const { topics, statistics, ...showData } = data;
      const debates = !topics?.length ? [] : topics.map((topic) => topic.id);

      if (statistics.topUsers?.length) {
        const { usersData, topUsersList } = topUsersMapper(statistics.topUsers);
        dispatch(
          showsSlice.actions.setShowDataById({
            id: show?.id || '',
            ...showData,
            statistics: {
              ...statistics,
              topUsers: topUsersList,
              debates,
            },
          }),
        );
        dispatch(usersSlice.actions.setUsersData(Object.values(usersData)));
      } else {
        dispatch(
          showsSlice.actions.setShowDataById({
            id: show?.id || '',
            ...showData,
            statistics: {
              ...statistics,
              debates,
            },
          }),
        );
      }
      dispatch(topicsSlice.actions.setTopicsData(topics));
    }
  };

  const handleEndShowCoHost = (data: {
    show: ShowServerData;
    topics: Topic[];
    statistics: HostStatistics & ParticipantStatistics;
  }) => {
    let topUsers: { userId: string; likesCount: number }[] = [];
    const { topics, statistics, show: showData } = data;

    if (statistics.topUsers?.length) {
      const { usersData, topUsersList } = topUsersMapper(statistics.topUsers);
      topUsers = topUsersList;
      dispatch(usersSlice.actions.setUsersData(Object.values(usersData)));
    }

    dispatch(
      showsSlice.actions.setShowDataById({
        ...showData,
        statistics: {
          ...statistics,
          topUsers,
          debates: !topics?.length ? [] : topics.map((topic) => topic.id),
        },
      }),
    );
    dispatch(topicsSlice.actions.setTopicsData(topics));
  };

  const handleSpeakerDataUpdate = (part: UpdateParticipantRoleSocketData) => {
    dispatch(usersSlice.actions.setUserDataById(part.profile));

    dispatch(
      speakersSlice.actions.setSpeakerDataById({
        id: part.speaker.id,
        side: part.speaker.side,
        likesCount: 0,
        createdAt: part.speaker.createdAt,
        profileId: part.profile.id,
        topicId: part.speaker.topic.id,
        participantId: part.id,
      }),
    );

    dispatch(
      participantsSlice.actions.setParticipantDataById({
        id: part.id,
        role: part.role,
        status: part.status,
        uid: part.uid,
        team: part.team,
        profileId: part.profile.id,
        showId: show?.id || '',
      }),
    );
  };

  return {
    handleParticipantJoin,
    handleAnonymousParticipantJoin,
    handleParticipantLeave,
    handleParticipantStatusUpdate,
    handleShowUpdate,
    handleEndShow,
    handleEndShowCoHost,
    handleSpeakerDataUpdate,
  };
};

export const useSocketHandlers = ({
  dispatch,
  user,
  show,
  rightSpeakerId,
  leftSpeakerId,
  onClientRoleChanged,
  turnOnMicrophone,
  isHost,
  isCoHost,
}: SocketHandlerDependencies) => {
  return useCallback(
    () =>
      createSocketHandlers({
        dispatch,
        user,
        show,
        rightSpeakerId,
        leftSpeakerId,
        onClientRoleChanged,
        turnOnMicrophone,
        isHost,
        isCoHost,
      }),
    [
      dispatch,
      user,
      show,
      rightSpeakerId,
      leftSpeakerId,
      onClientRoleChanged,
      turnOnMicrophone,
      isHost,
      isCoHost,
    ],
  );
};
